import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { environment } from '../../environments/environment';
import { Constants } from '../../constants';
import { Feature } from '../../models/feature';

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  message?: string;
}

@Injectable({
  providedIn: 'root'
})
export class AdminFeatureService {

  constructor(private http: HttpClient) {}

  private getRequestHeaders() {
    return new HttpHeaders({
      'Content-Type': 'application/json',
      'Accept': 'application/json',
      'Authorization': localStorage.getItem(Constants.LOCAL_TOKEN) ?? ''
    });
  }

  private get baseUrl() {
    return environment.apiUrl + '/admin/features';
  }

  /** GET /admin/features */
  async getFeatures(): Promise<ApiResponse<Feature[]>> {
    try {
      const res = await this.http.get<ApiResponse<Feature[]>>(this.baseUrl, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService getFeatures:', e);
      return { success: false };
    }
  }

  /** POST /admin/features – body: { code, name } (code is the id) */
  async addFeature(feature: { code: string; name: string }): Promise<ApiResponse<Feature>> {
    try {
      const res = await this.http.post<ApiResponse<Feature>>(this.baseUrl, feature, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService addFeature:', e);
      return { success: false };
    }
  }

  /** GET /admin/features/:code (code is the id) */
  async getFeatureById(code: string): Promise<ApiResponse<Feature>> {
    try {
      const res = await this.http.get<ApiResponse<Feature>>(`${this.baseUrl}/${code}`, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService getFeatureById:', e);
      return { success: false };
    }
  }

  /** PUT /admin/features/:code (code is the id) */
  async updateFeature(code: string, feature: Partial<Feature>): Promise<ApiResponse<Feature>> {
    try {
      const res = await this.http.put<ApiResponse<Feature>>(`${this.baseUrl}/${code}`, feature, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService updateFeature:', e);
      return { success: false };
    }
  }

  /** DELETE /admin/features/:code (code is the id) */
  async deleteFeature(code: string): Promise<ApiResponse<void>> {
    try {
      const res = await this.http.delete<ApiResponse<void>>(`${this.baseUrl}/${code}`, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService deleteFeature:', e);
      return { success: false };
    }
  }

  /** POST /admin/services - create new service/subscription
   * Payload: { name, monthlyPrice, annualPrice }
   * monthlyPrice/annualPrice pattern: ^[0-9]+(\.[0-9]{1,2})?$
   */
  async createService(service: {
    name: string;
    monthlyPrice: string;
    annualPrice?: string;
    recurrency?: 6 | 12 | 24;
    customFeeRate?: number | null;
    monthCount?: number | null;
  }): Promise<ApiResponse<{ id: string; name: string; price?: string; annualPrice?: string; customFeeRate?: number; monthCount?: number }>> {
    try {
      const url = environment.apiUrl + '/admin/services';
      const payload: Record<string, any> = {
        name: service.name,
        monthlyPrice: service.monthlyPrice
      };
      if (service.annualPrice?.trim()) {
        payload['annualPrice'] = service.annualPrice.trim();
      }
      if (service.recurrency) {
        payload['recurrency'] = service.recurrency;
      }
      payload['customFeeRate'] = service.customFeeRate ?? null;
      payload['monthCount'] = service.monthCount ?? null;
      const res = await this.http.post<ApiResponse<{ id: string; name: string; price?: string; annualPrice?: string; customFeeRate?: number; monthCount?: number }>>(url, payload, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService createService:', e);
      return { success: false };
    }
  }

  /** DELETE /services/:serviceId - remove subscription (admin token) */
  async deleteService(serviceId: string): Promise<ApiResponse<void>> {
    try {
      const url = environment.apiUrl + `/services/${serviceId}`;
      const res = await this.http.delete<ApiResponse<void>>(url, {
        headers: this.getRequestHeaders()
      }).toPromise() as { success: boolean; code?: string; };

      console.log('AdminFeatureService deleteService:', res);
      
      if(!res) {
        return { success: false, message: 'Errore durante l\'eliminazione' };
      }

      if(res.success) {
        return { success: true };
      } else {
        return { success: false, message: res.code == "SERVICE_IN_USE_BY_COMPANY" ? 'Questo abbonamento è in uso da una società' : 'Errore durante l\'eliminazione' };
      }
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService deleteService:', e);
      return { success: false };
    }
  }

  /** PUT /admin/services/:serviceId - update service (visible, name, price, etc.) */
  async updateService(
    serviceId: string,
    payload: { name?: string; price?: number; annualPrice?: number; status?: string; visible?: boolean; stripeMonthlyPriceId?: string; stripeAnnualPriceId?: string; customFeeRate?: number | null; monthCount?: number | null }
  ): Promise<ApiResponse<{ id: string; name: string; price?: string; annualPrice?: string; visible?: boolean; customFeeRate?: number; monthCount?: number }>> {
    try {
      const url = environment.apiUrl + `/services/${serviceId}`;
      const res = await this.http.put<ApiResponse<{ id: string; name: string; price?: string; annualPrice?: string; visible?: boolean }>>(url, payload, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService updateService:', e);
      return { success: false };
    }
  }

  /** GET /admin/services/:serviceId/features */
  async getServiceFeatures(serviceId: string): Promise<ApiResponse<Feature[]>> {
    try {
      const url = environment.apiUrl + `/admin/services/${serviceId}/features`;
      const res = await this.http.get<ApiResponse<Feature[]>>(url, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService getServiceFeatures:', e);
      return { success: false };
    }
  }

  /** POST /admin/services/:serviceId/features - body: { featureId } (featureId is the feature code) */
  async addFeatureToService(serviceId: string, featureCode: string): Promise<ApiResponse<Feature>> {
    try {
      const url = environment.apiUrl + `/admin/services/${serviceId}/features`;
      const res = await this.http.post<ApiResponse<Feature>>(url, { featureId: featureCode }, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService addFeatureToService:', e);
      return { success: false };
    }
  }

  /** DELETE /admin/services/:serviceId/features - featureCode is the feature code (id) */
  async removeFeatureFromService(serviceId: string, featureCode: string): Promise<ApiResponse<void>> {
    try {
      const url = environment.apiUrl + `/admin/services/${serviceId}/features?featureId=${encodeURIComponent(featureCode)}`;
      const res = await this.http.delete<ApiResponse<void>>(url, {
        headers: this.getRequestHeaders()
      }).toPromise();
      return res ?? { success: false };
    } catch (e) {
      console.error('AdminFeatureService removeFeatureFromService:', e);
      return { success: false };
    }
  }
}
